import { type FormEvent, memo, useEffect, useState } from 'react';
import { Button, ModalContentWrapper, Spinner } from '@/components/ui';
import { useUpdateTenantOrganizationSettings } from '@/hooks/use-tenant-settings';
import { cn } from '@/lib/cn';
import { extractErrorMessage, handleMutationError } from '@/lib/error-handling';
import {
  type OrganizationSettingsInput,
  organizationSettingsSchema,
  validateWithSchema,
} from '@/lib/schemas';
import { toast } from '@/stores/toast-store';

interface Props {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  initialValues: OrganizationSettingsInput;
}

function sanitizeOrganizationAcn(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 9);

  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;

  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}

export const OrganizationSettingsModal = memo(function OrganizationSettingsModal({
  open,
  onClose,
  tenantId,
  initialValues,
}: Props) {
  const updateOrganization = useUpdateTenantOrganizationSettings(tenantId);
  const [registerName, setRegisterName] = useState(initialValues.registerName);
  const [address, setAddress] = useState(initialValues.address ?? '');
  const [acn, setAcn] = useState(initialValues.acn ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }

    setRegisterName(initialValues.registerName);
    setAddress(initialValues.address ?? '');
    setAcn(initialValues.acn ?? '');
    setErrors({});
    setTouched({});
    setFormError('');
  }, [initialValues.acn, initialValues.address, initialValues.registerName, open]);

  function buildFormValues(
    overrides?: Partial<Record<'registerName' | 'address' | 'acn', string>>,
  ) {
    return {
      registerName: overrides?.registerName ?? registerName,
      address: overrides?.address ?? address,
      acn: overrides?.acn ?? acn,
    };
  }

  function clearError(path: string) {
    setErrors(current => {
      if (!current[path]) {
        return current;
      }

      const next = { ...current };
      delete next[path];
      return next;
    });
  }

  function validateField(
    path: keyof OrganizationSettingsInput,
    overrides?: Partial<Record<'registerName' | 'address' | 'acn', string>>,
  ) {
    const validation = validateWithSchema(organizationSettingsSchema, buildFormValues(overrides));

    setErrors(current => {
      const next = { ...current };
      delete next[path];

      if (!validation.success && validation.errors[path]) {
        next[path] = validation.errors[path];
      }

      return next;
    });
  }

  function markTouched(path: keyof OrganizationSettingsInput) {
    setTouched(current => ({ ...current, [path]: true }));
  }

  function updateField(
    path: keyof OrganizationSettingsInput,
    setter: (value: string) => void,
    value: string,
  ) {
    setter(value);
    if (touched[path] || errors[path]) {
      validateField(path, { [path]: value });
    } else {
      clearError(path);
    }
    setFormError('');
  }

  function getFieldError(path: string) {
    return errors[path];
  }

  function getInputClass(path: string) {
    return cn(
      inputClass,
      getFieldError(path)
        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
        : 'border-gray-300',
    );
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError('');

    const validation = validateWithSchema(organizationSettingsSchema, {
      registerName,
      address,
      acn,
    });

    if (!validation.success) {
      setErrors(validation.errors);
      const firstError = Object.values(validation.errors)[0];
      if (firstError) {
        toast.error(firstError);
      }
      return;
    }

    setErrors({});

    updateOrganization.mutate(validation.data, {
      onSuccess: () => {
        toast.success('Organization settings saved');
        onClose();
      },
      onError: (error: any) => {
        setFormError(extractErrorMessage(error));
        handleMutationError(error, 'Failed to save organization settings');
      },
    });
  }

  if (!open) {
    return null;
  }

  const inputClass =
    'w-full rounded-md border px-3 py-2 text-sm placeholder:text-gray-400 focus:ring-1 outline-none';

  return (
    <ModalContentWrapper open={open} onClose={onClose} title="Organization Settings">
      <form noValidate onSubmit={handleSubmit} className="space-y-4">
        {formError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {formError}
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Register Name *</label>
          <input
            type="text"
            value={registerName}
            onChange={event => updateField('registerName', setRegisterName, event.target.value)}
            onBlur={() => {
              markTouched('registerName');
              validateField('registerName');
            }}
            className={getInputClass('registerName')}
          />
          {getFieldError('registerName') && (
            <p className="mt-1 text-xs text-red-600">{getFieldError('registerName')}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Address (optional)</label>
          <input
            type="text"
            value={address}
            onChange={event => updateField('address', setAddress, event.target.value)}
            onBlur={() => {
              markTouched('address');
              validateField('address');
            }}
            placeholder="111/11 Name Street, North Sydney NSW 1111"
            className={getInputClass('address')}
          />
          {getFieldError('address') && (
            <p className="mt-1 text-xs text-red-600">{getFieldError('address')}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">ACN</label>
          <input
            type="text"
            value={acn}
            onChange={event =>
              updateField('acn', setAcn, sanitizeOrganizationAcn(event.target.value))
            }
            onBlur={() => {
              markTouched('acn');
              validateField('acn');
            }}
            placeholder="123456789"
            inputMode="numeric"
            autoComplete="off"
            maxLength={11}
            className={getInputClass('acn')}
          />
          {getFieldError('acn') && (
            <p className="mt-1 text-xs text-red-600">{getFieldError('acn')}</p>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} className="px-4 py-2">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={updateOrganization.isPending}
            className="flex items-center gap-2 px-4 py-2 text-white"
          >
            {updateOrganization.isPending ? (
              <Spinner size="lg" className="w-4 h-4 animate-spin" />
            ) : null}
            Save
          </Button>
        </div>
      </form>
    </ModalContentWrapper>
  );
});
